<template>
  <v-row class="my-6" no-gutters>
    <v-col cols="12">
      <v-row align="stretch">
        <v-col cols="6" class="py-0">
          <div v-if="processedHtml" class="html-email-preview">
            <div v-html="processedHtml" />
          </div>
          <v-row v-else class="background-html-empty-preview" align="center" no-gutters>
            <v-col class="img-text text-center">
              <mf-icon size="60">code</mf-icon>
              <p class="primary-text">Adicione um HTML</p>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="6" class="py-0">
          <v-card style="height: 100%">
            <v-col class="pa-4">
              <p class="email-marketing-card-title">Upload de arquivo</p>
              <p class="primary-text text-justify">
                Selecione um arquivo .html para atrelar a campanha. <br />
                <span>
                  Lembre-se, para importar layout de e-mail na plataforma Mercafacil, suas imagens precisam estar hosteadas em alguma plataforma pública, ou
                  seja, precisam estar dentro do seu arquivo HTML em formato de link. Arquivos no formato ZIP, com imagens referenciadas não serão aceitas.
                </span>
              </p>
              <v-tooltip top :disabled="disableTooltip">
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <input id="html-file" ref="html-file" class="hidden-input" accept=".html" type="file" @input="readHtmlFile($event)" />
                    <mf-button :disabled="disabled" :loading="isLoading" label="Selecionar arquivo HTML" @click="setHtmlFile" />&nbsp;
                    <span v-if="urlUploadedFile" class="file-chip">
                      <mf-icon fill small color="marca-0">insert_drive_file</mf-icon>
                      {{ fileName }}
                      <mf-icon :disabled="disabled" :style="{ fontSize: '11px' }" icon="close" @click="deleteFile" />
                    </span>
                    <p class="hint-text mt-2">Formato aceito: HTML.</p>
                  </div>
                </template>
                <span>{{ disabledFieldTooltip }}</span>
              </v-tooltip>
            </v-col>

            <v-row no-gutters>
              <v-col class="pa-4">
                <div class="d-flex justify-space-between">
                  <p class="email-marketing-card-title">Código HTML</p>
                  <message-variables channel="email" />
                </div>
                <v-tooltip top :disabled="disableTooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <v-textarea
                        v-model="processedHtml"
                        class="textarea"
                        placeholder="Inserir HTML"
                        outlined
                        no-resize
                        rows="10"
                        :disabled="disabled"
                        :error-messages="errorProcessedHtmlText"
                      />
                    </div>
                  </template>
                  <span v-if="isMariaCampaignActivated || isMariaCampaignFinished">{{ disabledFieldTooltip }}</span>
                  <span v-else>Habilite este canal pela caixa de seleção acima para configurá-lo.</span>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { characterVariables } from '@/mixins/character-variables'
import { MUTATION_SINGLE_UPLOAD } from '@/modules/configuracoes-admin/graphql'
export default {
  name: 'HtmlEmailComposer',
  components: {
    MessageVariables: () => import('@/modules/campanhas/views/components/edit/MessageVariables.vue'),
  },
  mixins: [characterVariables],
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    isMariaCampaignActivated: {
      type: Boolean,
      default: false,
    },
    isMariaCampaignFinished: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    validations: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    file: '',
    urlUploadedFile: '',
    processedHtml: '',
    isLoading: false,
  }),
  computed: {
    campaign: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      },
    },
    disabledFieldTooltip() {
      const status = this.isMariaCampaignFinished ? 'concluída' : 'ativa'
      return `Não é possível alterar essa configuração em uma campanha ${status}.`
    },
    errorProcessedHtmlText() {
      let validations = null
      if (!this.isMariaCampaign) validations = this.validations.campaign.message
      if (this.validations.campaign?.shotConfiguration?.email) validations = this.validations.campaign.shotConfiguration.email.message

      if (!validations || !validations.processed_html) return ''
      if (validations.processed_html.$error) {
        if (!validations.processed_html.required) return 'Adicione ou importe um template HTML.'
        else if (!validations.processed_html.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    disableTooltip() {
      if (!this.isMariaCampaign) return true
      return !this.disabled
    },
    fileName() {
      let splitName = this.urlUploadedFile.split('/').pop().split('-')
      if (this.urlUploadedFile.split('/').length === 1) return this.urlUploadedFile
      return splitName.slice(5, splitName.length).join('-')
    },
  },
  watch: {
    processedHtml() {
      this.campaign.message.processed_html = this.processedHtml
    },
  },
  mounted() {
    this.processedHtml = this.campaign.message.processed_html
    this.urlUploadedFile = this.campaign.message.html_email_configuration?.url
  },
  methods: {
    async readHtmlFile(event) {
      this.isLoading = true
      this.file = event.target.files[0]
      try {
        const {
          data: { singleUpload },
        } = await this.$apollo.mutate({
          mutation: MUTATION_SINGLE_UPLOAD,
          variables: {
            file: this.file,
          },
          context: {
            uri: this.$microservicesUrls['upload'],
          },
        })
        this.readHtml(this.file)
        this.urlUploadedFile = singleUpload.url
        this.campaign.message.html_email_configuration = {
          url: singleUpload.url,
          filename: this.fileName,
          has_uploaded_file: true,
          uploaded_at: new Date(),
        }
        this.isLoading = false
      } catch (e) {
        this.$snackbar({ message: `Falha no upload de arquivo: ${e.message}` })
        this.isLoading = false
      }
    },

    readHtml(file) {
      const fileReader = new FileReader()
      const that = this
      fileReader.onload = function (event) {
        that.processedHtml = event.target.result
      }
      fileReader.readAsText(file)
    },

    deleteFile() {
      this.file = ''
      this.urlUploadedFile = ''
      this.processedHtml = ''
    },
    setHtmlFile() {
      this.$refs['html-file'].click()
      document.querySelector('#html-file').value = ''
    },
    getConfiguration() {
      return {}
    },
    isTouched() {
      if (this.processedHtml) {
        return true
      }
      if (this.campaign?.message?.html_email_configuration?.has_uploaded_file) {
        return true
      }
      return false
    },
  },
}
</script>
<style lang="scss">
$primary-color: $cinza-l70;
.background-html-empty-preview {
  background-color: $primary-color;
  height: 558px;
}
.email-marketing-card-title {
  margin-bottom: 8px;
  font-size: 18px;
  font-weight: bold;
  letter-spacing: 0.33px;
  color: $cinza-d20;
}
.html-email-preview {
  background-color: #fff;
  max-height: 558px;
  overflow-y: auto;
}
.hidden-input {
  display: none;
}
.file-chip {
  word-break: break-all;
}
.hint-text {
  font-size: 14px;
  line-height: 21px;
  &.external {
    max-width: 700px;
  }
  b {
    color: $marca-1;
    font-weight: 700;
  }
}
</style>
